import * as d3 from 'd3';
import type React from 'react';
import { getChartChrome } from '../../../../../theme/chartChrome';
import type { PowerDataPoint } from '../utils';
import { formatTimeLabel } from '../utils';

export interface ZoomConfig {
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  g: d3.Selection<SVGGElement, unknown, null, undefined>;
  plotGroup: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  yAxis: d3.Axis<d3.NumberValue>;
  plotWidth: number;
  plotHeight: number;
  windowStart: number;
  windowEnd: number;
  timeExtent: [number, number];
  animationRange: number;
  isPlaying: boolean;
  perStrokeMode: boolean;
  sessionId: string;
  allStrokeCycles: Array<{ start: number; end: number; strokeNumber: number }>;
  renderRowerKeys: string[];
  responsiveFontSizes: { strokeNumber: number; axisTick: number; axisLabel: number };
  zoomRef: React.MutableRefObject<d3.ZoomBehavior<SVGSVGElement, unknown> | null>;
  zoomTransformRef: React.MutableRefObject<d3.ZoomTransform>;
  /** Called when user zooms/pans so the component can lock the visible window. */
  onZoomToWindow?: (windowStart: number, windowEnd: number) => void;
  /** When true, do not restore a previous zoom transform (view is driven by state). */
  useLockedView?: boolean;
}

export function setupZoomBehavior(config: ZoomConfig): {
  behavior: d3.ZoomBehavior<SVGSVGElement, unknown>;
  cleanup: () => void;
} {
  const {
    svg,
    g,
    plotGroup,
    xScale,
    yScale,
    yAxis,
    plotWidth,
    plotHeight,
    windowStart,
    windowEnd,
    timeExtent,
    animationRange,
    isPlaying,
    perStrokeMode,
    sessionId,
    allStrokeCycles,
    renderRowerKeys,
    responsiveFontSizes,
    zoomRef,
    zoomTransformRef,
    onZoomToWindow,
    useLockedView = false,
  } = config;

  const currentWindowSize = windowEnd - windowStart;
  const minScale = Math.max(0.05, currentWindowSize / animationRange);
  const zoomBehavior = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([minScale, 20])
    .translateExtent(
      isPlaying
        ? [
            [0, 0],
            [plotWidth, plotHeight],
          ]
        : [
            [-Infinity, -Infinity],
            [Infinity, Infinity],
          ]
    )
    .extent([
      [0, 0],
      [plotWidth, plotHeight],
    ])
    .filter((event) => {
      const type = event?.type || '';
      const isWheel = type === 'wheel';
      const isTouch = type === 'touchstart' || type === 'touchmove' || type === 'touchend';
      // Keep trackpad/page scrolling as the default. Require Shift+wheel to zoom.
      if (isWheel && !event?.shiftKey) {
        return false;
      }
      if (event?.shiftKey && !isWheel && !isTouch) {
        return false;
      }
      return true;
    })
    .on('zoom', (event) => {
      if (event.sourceEvent) {
        svg.interrupt();
      }

      zoomTransformRef.current = event.transform;

      let transform = event.transform;
      const newXScale = transform.rescaleX(xScale);
      const [domainMin, domainMax] = newXScale.domain();

      if (isPlaying) {
        if (domainMin < windowStart) {
          const correction = xScale(windowStart) * transform.k;
          transform = d3.zoomIdentity.translate(correction, transform.y).scale(transform.k);
          zoomTransformRef.current = transform;
        }

        if (domainMax > windowEnd) {
          const correction = xScale(windowEnd) * transform.k - plotWidth * transform.k;
          transform = d3.zoomIdentity.translate(-correction, transform.y).scale(transform.k);
          zoomTransformRef.current = transform;
        }
      } else {
        const maxNegativeMargin = -1.0;
        if (domainMin < maxNegativeMargin) {
          const correction = xScale(maxNegativeMargin) * transform.k;
          transform = d3.zoomIdentity.translate(correction, transform.y).scale(transform.k);
          zoomTransformRef.current = transform;
        }

        if (domainMax > timeExtent[1]) {
          const correction = xScale(timeExtent[1]) * transform.k - plotWidth * transform.k;
          transform = d3.zoomIdentity.translate(-correction, transform.y).scale(transform.k);
          zoomTransformRef.current = transform;
        }
      }

      const constrainedXScale = transform.rescaleX(xScale);

      const chrome = getChartChrome();

      // Capture the ACTUAL visible window (after constraints) so play/arrows don't reset the view
      if (event.sourceEvent && onZoomToWindow) {
        const [visibleStart, visibleEnd] = constrainedXScale.domain();
        onZoomToWindow(visibleStart, visibleEnd);
      }

      g.select<SVGGElement>('.x-axis')
        .call(
          d3
            .axisBottom(constrainedXScale)
            .ticks(10)
            .tickFormat(formatTimeLabel(constrainedXScale) as any)
        )
        .call((g) =>
          g
            .selectAll('text')
            .attr('font-size', responsiveFontSizes.axisTick)
            .attr('fill', chrome.axisTick)
            .attr('dy', '0.71em')
            .style('pointer-events', 'none')
        );

      g.select<SVGGElement>('.y-axis')
        .call(yAxis as any)
        .call((g) =>
          g
            .selectAll('text')
            .attr('font-size', responsiveFontSizes.axisTick)
            .attr('font-family', 'Inter, sans-serif')
            .attr('fill', chrome.axisTick)
            .attr('dx', '-0.5em')
            .style('pointer-events', 'none')
        );

      const xGridGroup = plotGroup.selectAll('.grid').filter(function () {
        const firstLine = d3.select(this).select('line').node() as SVGLineElement | null;
        return firstLine && firstLine.getAttribute('x1') !== null;
      });
      xGridGroup
        .selectAll('line')
        .data(constrainedXScale.ticks(10))
        .attr('x1', (d) => constrainedXScale(d as number))
        .attr('x2', (d) => constrainedXScale(d as number));

      const newLine = d3
        .line<PowerDataPoint>()
        .defined((d) => d != null && isFinite((d as PowerDataPoint).time))
        .x((d) => constrainedXScale((d as PowerDataPoint).time))
        .curve(d3.curveCatmullRom.alpha(0.5));

      renderRowerKeys.forEach((rowerKey, index) => {
        const newLinePath = newLine.y((d) => yScale((d as PowerDataPoint)[rowerKey] as number));

        plotGroup.select(`.ghost-line-${index}`).attr('d', newLinePath as any);
        plotGroup.select(`.active-line-${index}`).attr('d', newLinePath as any);
      });

      const strokeCyclesGroup = plotGroup.select('.stroke-cycles');
      if (!strokeCyclesGroup.empty() && allStrokeCycles && allStrokeCycles.length > 0) {
        const [domainMin, domainMax] = constrainedXScale.domain();
        interface StrokeCycle {
          start: number;
          end: number;
          strokeNumber: number;
        }
        const visibleStrokes = allStrokeCycles.filter((stroke: StrokeCycle) => {
          return stroke.end >= domainMin && stroke.start <= domainMax;
        });

        const rects = strokeCyclesGroup
          .selectAll<SVGRectElement, StrokeCycle>('rect')
          .data(visibleStrokes);

        rects.exit().remove();

        rects
          .attr('x', (d) => constrainedXScale(d.start))
          .attr('width', (d) => {
            const width = constrainedXScale(d.end) - constrainedXScale(d.start);
            return Math.max(1, width);
          })
          .attr(
            'clip-path',
            isPlaying && !perStrokeMode ? `url(#reveal-clip-${sessionId})` : 'none'
          );

        rects
          .enter()
          .append('rect')
          .attr('x', (d) => constrainedXScale(d.start))
          .attr('width', (d) => {
            const width = constrainedXScale(d.end) - constrainedXScale(d.start);
            return Math.max(1, width);
          })
          .attr('y', 0)
          .attr('height', plotHeight)
          .attr('fill', '#3B82F6')
          .attr('opacity', 0.12)
          .attr('stroke', '#3B82F6')
          .attr('stroke-width', 0.5)
          .attr('stroke-opacity', 0.2)
          .attr(
            'clip-path',
            isPlaying && !perStrokeMode ? `url(#reveal-clip-${sessionId})` : 'none'
          )
          .style('pointer-events', 'none');

        const labels = strokeCyclesGroup.selectAll<
          SVGTextElement,
          StrokeCycle & { strokeNumber: number }
        >('text.stroke-number');
        const labelsData = visibleStrokes.map((stroke) => ({
          ...stroke,
          strokeNumber: stroke.strokeNumber || 0,
        }));

        const labelsUpdate = labels.data(
          labelsData,
          (d: any) => `stroke-${d.strokeNumber}-${d.start}-${d.end}`
        );

        labelsUpdate.exit().remove();

        labelsUpdate
          .attr('x', (d) => {
            const centerX =
              constrainedXScale(d.start) +
              (constrainedXScale(d.end) - constrainedXScale(d.start)) / 2;
            return centerX;
          })
          .attr(
            'clip-path',
            isPlaying && !perStrokeMode ? `url(#reveal-clip-${sessionId})` : 'none'
          )
          .text((d) => d.strokeNumber);

        labelsUpdate
          .enter()
          .append('text')
          .attr('class', 'stroke-number')
          .attr('x', (d) => {
            const centerX =
              constrainedXScale(d.start) +
              (constrainedXScale(d.end) - constrainedXScale(d.start)) / 2;
            return centerX;
          })
          .attr('y', 45)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'baseline')
          .attr('fill', chrome.axisTick)
          .attr('font-size', responsiveFontSizes.strokeNumber)
          .attr('font-weight', 'bold')
          .attr('font-family', 'Inter, sans-serif')
          .attr(
            'clip-path',
            isPlaying && !perStrokeMode ? `url(#reveal-clip-${sessionId})` : 'none'
          )
          .style('pointer-events', 'none')
          .text((d) => d.strokeNumber);
      }
    });

  zoomRef.current = zoomBehavior;
  svg.call(zoomBehavior as any);

  const currentSvgTransform = d3.zoomTransform(svg.node() as Element);

  // Never restore a previous zoom when view is state-driven (arrow keys / play); it would override the locked window
  if (
    !useLockedView &&
    (zoomTransformRef.current.k !== 1 ||
      zoomTransformRef.current.x !== 0 ||
      zoomTransformRef.current.y !== 0) &&
    currentSvgTransform.k === 1 &&
    currentSvgTransform.x === 0 &&
    currentSvgTransform.y === 0
  ) {
    svg.call(zoomBehavior.transform, zoomTransformRef.current);
  }

  return {
    behavior: zoomBehavior,
    cleanup: () => {},
  };
}
